import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton, Table, Tag } from '@digdir/designsystemet-react';
import { useGetUserDataExport } from '../../../hooks/queries/useGetUserDataExport';
import type { UserDataExport } from '../../../api/gdpr';
import { downloadExport, longDate, newestFirst, shortDate, TEAMS_SHOWN } from '../privacyView';
import { SettingsSection } from '../../../components/TeamSettingsLayout';
import classes from '../SettingsPage.module.css';

const Facts = ({ rows }: { rows: [string, React.ReactNode][] }) => (
  <dl className={classes.facts}>
    {rows.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ))}
  </dl>
);

const DatedList = ({ items }: { items: { key: string; title: string; date: string; detail?: string }[] }) => (
  <ul className={classes.datedList}>
    {items.map((item) => (
      <li key={item.key}>
        <span className={classes.itemTitle}>{item.title}</span>
        <span className={classes.subtle}>{item.date}</span>
        {item.detail && <span className={classes.itemDetail}>{item.detail}</span>}
      </li>
    ))}
  </ul>
);

const Data = ({ data }: { data: UserDataExport }) => {
  const { t, i18n } = useTranslation();
  const [downloaded, setDownloaded] = useState(false);
  const [allTeams, setAllTeams] = useState(false);
  const language = i18n.language;
  const roleName = (role: string) => t(`settings.privacy.roles.${role}`, { defaultValue: role });
  const from = (date: string) => t('settings.privacy.from', { date: longDate(date, language) });
  const { profile, workEnvironment } = data;
  const teams = allTeams ? data.teams : data.teams.slice(0, TEAMS_SHOWN);
  const none = t('settings.privacy.none');
  const count = (n: number) => (n === 0 ? none : String(n));
  const survey = (title: string) => t('settings.privacy.activity.survey', { title });
  const detail = (...parts: (string | null | false)[]) => parts.filter(Boolean).join(' · ');
  const activities = [
    {
      title: t('settings.privacy.activity.responsible'),
      items: workEnvironment.responsibleForMeasures.map((measure) => ({
        key: `measure-${measure.measureId}`,
        title: measure.title,
        date: t('settings.privacy.activity.deadline', { date: longDate(measure.dueDate, language) }),
        detail: detail(survey(measure.surveyTitle), t(`settings.privacy.activity.status.${measure.status}`)),
      })),
    },
    {
      title: t('settings.privacy.activity.proposals'),
      items: workEnvironment.proposals.map((proposal) => ({
        key: `proposal-${proposal.proposalId}`,
        title: proposal.title,
        date: shortDate(proposal.createdAt, language),
        detail: detail(survey(proposal.surveyTitle), proposal.reason),
      })),
    },
    {
      title: t('settings.privacy.activity.comments'),
      items: workEnvironment.planComments.map((comment) => ({
        key: `comment-${comment.commentId}`,
        title: comment.text,
        date: shortDate(comment.createdAt, language),
        detail: survey(comment.surveyTitle),
      })),
    },
    {
      title: t('settings.privacy.activity.approvals'),
      items: workEnvironment.approvedPlans.map((plan) => ({
        key: `plan-${plan.planId}`,
        title: t('settings.privacy.activity.approvedPlan', { title: plan.surveyTitle }),
        date: shortDate(plan.approvedAt, language),
        detail: detail(plan.deviation && t('settings.privacy.activity.deviation', { reason: plan.deviation })),
      })),
    },
    {
      title: t('settings.privacy.activity.decisions'),
      items: workEnvironment.areaDecisions.map((decision) => ({
        key: `decision-${decision.decisionId}`,
        title: t(`settings.privacy.activity.decision.${decision.decision}`),
        date: shortDate(decision.decidedAt, language),
        detail: detail(survey(decision.surveyTitle), decision.reason),
      })),
    },
  ];

  return (
    <>
      <SettingsSection
        headingId="privacy-download"
        title={t('settings.privacy.download.title')}
        description={t('settings.privacy.download.description')}
        action={
          <Button
            onClick={() => {
              downloadExport(data);
              setDownloaded(true);
            }}
          >
            {t('settings.privacy.download.button')}
          </Button>
        }
      />
      {downloaded && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('settings.privacy.download.done')}</Paragraph>
        </Alert>
      )}

      <Alert data-color="info">
        <Heading level={2} data-size="2xs">
          {t('settings.privacy.anonymous.title')}
        </Heading>
        <Paragraph>{t('settings.privacy.anonymous.description')}</Paragraph>
      </Alert>

      <SettingsSection headingId="privacy-profile" title={t('settings.privacy.profile.title')}>
        <Facts
          rows={[
            [t('settings.privacy.profile.email'), profile.email],
            [t('settings.privacy.profile.organization'), profile.tenantName],
            [
              t('settings.privacy.profile.status'),
              <Tag data-size="sm" data-color={profile.isPending ? 'warning' : 'success'}>
                {t(profile.isPending ? 'settings.privacy.profile.pending' : 'settings.privacy.profile.active')}
              </Tag>,
            ],
            [t('settings.privacy.profile.created'), longDate(profile.createdAt, language)],
            [t('settings.privacy.profile.updated'), longDate(profile.updatedAt, language)],
            [t('settings.privacy.profile.userId'), <span className={classes.userId}>{profile.auth0Id}</span>],
          ]}
        />
      </SettingsSection>

      <SettingsSection headingId="privacy-teams" title={t('settings.privacy.teams.title')}>
        <div className={classes.subsection}>
          <Heading level={3} data-size="2xs">
            {t('settings.privacy.teams.roles')}
          </Heading>
          {data.roles.length === 0 ? (
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('settings.privacy.teams.noRoles')}
            </Paragraph>
          ) : (
            <DatedList
              items={data.roles.map((role) => ({ key: `${role.role}-${role.assignedAt}`, title: roleName(role.role), date: from(role.assignedAt) }))}
            />
          )}
        </div>
        <div className={classes.subsection}>
          <Heading level={3} data-size="2xs">
            {t('settings.privacy.teams.teams', { count: data.teams.length })}
          </Heading>
          {data.teams.length === 0 ? (
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('settings.privacy.teams.noTeams')}
            </Paragraph>
          ) : (
            <>
              <div className={classes.tableScroll}>
                <Table data-size="sm" className={classes.table}>
                  <Table.Head>
                    <Table.Row>
                      <Table.HeaderCell>{t('settings.privacy.teams.team')}</Table.HeaderCell>
                      <Table.HeaderCell>{t('settings.privacy.teams.role')}</Table.HeaderCell>
                      <Table.HeaderCell>{t('settings.privacy.teams.since')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {teams.map((team) => (
                      <Table.Row key={team.teamId}>
                        <Table.Cell>{team.teamName}</Table.Cell>
                        <Table.Cell>{team.isLeader ? roleName('TEAM_LEAD') : roleName(team.role)}</Table.Cell>
                        <Table.Cell>{shortDate(team.joinedAt, language)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              {data.teams.length > TEAMS_SHOWN && (
                <Button variant="tertiary" data-size="sm" className={classes.startButton} onClick={() => setAllTeams((all) => !all)}>
                  {allTeams ? t('settings.privacy.teams.fewer') : t('settings.privacy.teams.all', { count: data.teams.length })}
                </Button>
              )}
            </>
          )}
        </div>
      </SettingsSection>

      <SettingsSection headingId="privacy-activity" title={t('settings.privacy.activity.title')}>
        <dl className={classes.activityStats}>
          {activities.map(({ title, items }) => (
            <div key={title}>
              <dt>{title}</dt>
              <dd>{count(items.length)}</dd>
            </div>
          ))}
        </dl>
        {activities
          .filter(({ items }) => items.length > 0)
          .map(({ title, items }) => (
            <div key={title} className={classes.subsection}>
              <Heading level={3} data-size="2xs">
                {title}
              </Heading>
              <DatedList items={items} />
            </div>
          ))}
        <div className={classes.subsection}>
          <Heading level={3} data-size="2xs">
            {t('settings.privacy.activity.guides')}
          </Heading>
          {data.guides.length === 0 ? (
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('settings.privacy.activity.noGuides')}
            </Paragraph>
          ) : (
            <DatedList
              items={data.guides.map((guide) => ({
                key: `guide-${guide.guideId}`,
                title: guide.title || t('settings.privacy.activity.guide'),
                date: from(guide.accessGrantedAt),
              }))}
            />
          )}
        </div>
        <div className={classes.subsection}>
          <Heading level={3} data-size="2xs">
            {t('settings.privacy.activity.log')}
          </Heading>
          {data.activityLog.length === 0 ? (
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('settings.privacy.activity.noLog')}
            </Paragraph>
          ) : (
            <DatedList
              items={newestFirst(data.activityLog, (entry) => entry.timestamp).map((entry, index) => ({
                key: `log-${index}`,
                title: t(`settings.privacy.activity.actions.${entry.action}`, { defaultValue: entry.action }),
                date: shortDate(entry.timestamp, language),
                detail: [
                  entry.teamName,
                  entry.surveyTitle && t('settings.privacy.activity.survey', { title: entry.surveyTitle }),
                ]
                  .filter(Boolean)
                  .join(' · '),
              }))}
            />
          )}
        </div>
      </SettingsSection>

      <SettingsSection headingId="privacy-why" title={t('settings.privacy.why.title')}>
        <Facts
          rows={[
            [t('settings.privacy.why.basis'), t('settings.privacy.why.basisText')],
            [t('settings.privacy.why.purpose'), t('settings.privacy.why.purposeText')],
            [t('settings.privacy.why.recipients'), t('settings.privacy.why.recipientsText')],
            [t('settings.privacy.why.source'), t('settings.privacy.why.sourceText')],
            [t('settings.privacy.why.retention'), t('settings.privacy.why.retentionText')],
          ]}
        />
      </SettingsSection>

      <SettingsSection headingId="privacy-rights" title={t('settings.privacy.rights.title')} description={t('settings.privacy.rights.description')}>
        <ul className={classes.rights}>
          {(['correction', 'deletion', 'copy'] as const).map((right) => (
            <li key={right}>
              <span className={classes.itemTitle}>{t(`settings.privacy.rights.${right}.title`)}</span>
              <span className={classes.subtle}>{t(`settings.privacy.rights.${right}.text`)}</span>
            </li>
          ))}
          <li>
            <span className={classes.itemTitle}>{t('settings.privacy.rights.objection.title')}</span>
            <span className={classes.subtle}>
              <Trans
                i18nKey="settings.privacy.rights.objection.text"
                components={[<a key="0" href="mailto:hei@trustpulse.no" className="ds-link" />]}
              />
            </span>
          </li>
        </ul>
      </SettingsSection>
    </>
  );
};

/** Alt vi har lagret om brukeren, og hvordan de laster det ned */
export const PrivacyTab = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetUserDataExport();

  if (isLoading) {
    return (
      <div aria-busy="true" className={classes.stack}>
        <span className="ds-sr-only">{t('settings.privacy.loading')}</span>
        <Skeleton variant="rectangle" height="140px" />
        <Skeleton variant="rectangle" height="240px" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert data-color="danger">
        <Heading level={2} data-size="xs">
          {t('settings.privacy.error.title')}
        </Heading>
        <Paragraph>{t('settings.privacy.error.message')}</Paragraph>
      </Alert>
    );
  }

  return <Data data={data} />;
};
